import React, { useState, useEffect } from 'react';
import agentService from '../services/agentService';
import './Settings.css';

const AGENT_PARENT_API_URL = process.env.REACT_APP_AGENT_PARENT_API_URL || 'https://your-agent-parent-api-url.execute-api.us-east-1.amazonaws.com/prod';

function Settings({ isOpen, onClose, authResult }) {
  const [userArn, setUserArn] = useState(null);
  const [authenticatedRoleArn, setAuthenticatedRoleArn] = useState(null);
  const [editingArn, setEditingArn] = useState(false);
  const [newArn, setNewArn] = useState('');
  const [arnError, setArnError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadUserArn();
      loadAuthenticatedRoleArn();
    }
  }, [isOpen]);

  const loadUserArn = async () => {
    setArnError('');
    try {
      const arnData = await agentService.getUserArn(AGENT_PARENT_API_URL);
      if (arnData) {
        setUserArn(arnData.roleArn);
      }
    } catch (err) {
      console.log('No user ARN set:', err.message);
    }
  };

  const loadAuthenticatedRoleArn = async () => {
    try {
      const response = await fetch(`${AGENT_PARENT_API_URL}/config`);
      if (response.ok) {
        const config = await response.json();
        setAuthenticatedRoleArn(config.authenticatedRoleArn);
      }
    } catch (err) {
      console.error('Error loading authenticated role ARN:', err);
    }
  };

  const handleSaveArn = async () => {
    setArnError('');
    try {
      await agentService.putUserArn(AGENT_PARENT_API_URL, newArn);
      setUserArn(newArn);
      setEditingArn(false);
      setNewArn('');
    } catch (err) {
      setArnError('Failed to save ARN: ' + err.message);
    }
  };

  const handleDeleteArn = async () => {
    if (!window.confirm('Are you sure you want to delete your IAM role ARN?')) {
      return;
    }
    setArnError('');
    try {
      await agentService.deleteUserArn(AGENT_PARENT_API_URL);
      setUserArn(null);
    } catch (err) {
      setArnError('Failed to delete ARN: ' + err.message);
    }
  };

  const handleEditArn = () => {
    setNewArn(userArn || '');
    setEditingArn(true);
    setArnError('');
  };

  const handleCancelEdit = () => {
    setEditingArn(false);
    setNewArn('');
    setArnError('');
  };

  if (!isOpen) return null;

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>Settings</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="settings-content">
          <div className="settings-section">
            <h3>IAM Role ARN Configuration</h3>
            <p className="settings-description">
              Configure an IAM role ARN with Bedrock permissions to use in the frontend.
            </p>
            
            {arnError && <div className="error-message">{arnError}</div>}
            
            {!editingArn ? (
              <div className="arn-display">
                {userArn ? (
                  <>
                    <div className="arn-value">
                      <strong>Current ARN:</strong>
                      <code>{userArn}</code>
                    </div>
                    <div className="arn-actions">
                      <button onClick={handleEditArn} className="edit-button">
                        Edit ARN
                      </button>
                      <button onClick={handleDeleteArn} className="delete-button">
                        Delete ARN
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="no-arn">
                    <p>No IAM role ARN configured.</p>
                    <button onClick={handleEditArn} className="add-button">
                      Add ARN
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="arn-edit">
                <label>IAM Role ARN</label>
                <input
                  type="text"
                  value={newArn}
                  onChange={(e) => setNewArn(e.target.value)}
                  placeholder="arn:aws:iam::123456789012:role/YourRoleName"
                  className="arn-input"
                />
                <small>Enter the ARN of an IAM role with necessary Bedrock permissions</small>
                <div className="edit-actions">
                  <button onClick={handleSaveArn} className="save-button">
                    Save
                  </button>
                  <button onClick={handleCancelEdit} className="cancel-button">
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {userArn && authenticatedRoleArn && !editingArn && (
              <>
                <div className="trust-policy-section">
                  <h4>Trust Policy Required</h4>
                  <p className="trust-policy-description">
                    Add this trust policy to your IAM role (<code>{userArn}</code>) to allow Cognito authenticated users to assume it:
                  </p>
                  <div className="trust-policy-box">
                    <pre>{`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Principal": {
        "AWS": "${authenticatedRoleArn}"
      },
      "Action": "sts:AssumeRole"
    }
  ]
}`}</pre>
                    <button 
                      className="copy-button"
                      onClick={() => {
                        navigator.clipboard.writeText(`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Principal": {
        "AWS": "${authenticatedRoleArn}"
      },
      "Action": "sts:AssumeRole"
    }
  ]
}`);
                      }}
                    >
                      Copy Trust Policy
                    </button>
                  </div>
                  <small className="trust-policy-help">
                    This trust policy allows the Cognito authenticated role (<code>{authenticatedRoleArn}</code>) to assume your IAM role.
                  </small>
                </div>

                <div className="permissions-policy-section">
                  <h4>Bedrock Permissions Policy Required</h4>
                  <p className="trust-policy-description">
                    Add this permissions policy to your IAM role to allow Bedrock model invocation:
                  </p>
                  <div className="trust-policy-box">
                    <pre>{`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": [
        "bedrock:InvokeModel",
        "bedrock:InvokeModelWithResponseStream"
      ],
      "Resource": "*"
    }
  ]
}`}</pre>
                    <button 
                      className="copy-button"
                      onClick={() => {
                        navigator.clipboard.writeText(`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": [
        "bedrock:InvokeModel",
        "bedrock:InvokeModelWithResponseStream"
      ],
      "Resource": "*"
    }
  ]
}`);
                      }}
                    >
                      Copy Permissions Policy
                    </button>
                  </div>
                  <small className="trust-policy-help">
                    This permissions policy grants access to invoke Bedrock models. You can restrict the Resource to specific model ARNs if needed.
                  </small>
                </div>
              </>
            )}
          </div>

          <div className="settings-section">
            <h3>Access Token (for testing)</h3>
            <textarea
              readOnly
              value={authResult.accessToken}
              rows={5}
              onClick={(e) => e.target.select()}
              className="token-textarea"
            />
            <small>Use this token with the Authorization: Bearer header for API calls</small>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
